import { useState, useMemo, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  Typography,
  Button,
  TablePagination,
  alpha,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import StoreIcon from '@mui/icons-material/Store';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import Badge from '@mui/material/Badge';
import axiosInstance from '../../lib/axios';
import { TableToolbar, FilterOption, ColumnConfig } from '../../components/TableToolbar';
import { ResponsiveTable, Column } from '../../components/mobile/ResponsiveTable';
import type { StockTransfer, Location } from '../../types';
import { palette, typography, theme } from '../../theme/colors';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { useAnimationStyles } from '../../hooks/useAnimations';

interface StockTransferWithLocation extends StockTransfer {
  fromLocationName?: string;
  toLocationName?: string;
}

const fetchStockTransfers = async (): Promise<StockTransfer[]> => {
  const response = await axiosInstance.get('/stock-transfers/all');
  return Array.isArray(response.data) ? response.data : response.data.data || [];
};

const fetchLocations = async (): Promise<Location[]> => {
  const response = await axiosInstance.get('/locations/admin/all');
  return Array.isArray(response.data) ? response.data : response.data.data || [];
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'pending':
      return palette.warning;
    case 'approved':
      return palette.info;
    case 'in_transit':
      return palette.primary;
    case 'partially_completed':
      return palette.info;
    case 'completed':
      return palette.success;
    case 'cancelled':
      return palette.error;
    default:
      return palette.textMuted;
  }
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'pending':
      return PendingActionsIcon;
    case 'approved':
      return CheckCircleOutlineIcon;
    case 'in_transit':
      return LocalShippingIcon;
    case 'completed':
      return CheckCircleOutlineIcon;
    case 'cancelled':
      return CancelOutlinedIcon;
    default:
      return SwapHorizIcon;
  }
};

const getStatusLabel = (status: string) => {
  return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

const exportColumns: ColumnConfig[] = [
  {
    field: 'productName',
    headerName: 'Product',
    valueFormatter: (_, row) => row.product?.name || 'Unknown',
  },
  { field: 'quantity', headerName: 'Quantity' },
  {
    field: 'fromLocationName',
    headerName: 'From',
    valueFormatter: (_, row) => row.fromLocationName || row.fromLocationId,
  },
  {
    field: 'toLocationName',
    headerName: 'To',
    valueFormatter: (_, row) => row.toLocationName || row.toLocationId,
  },
  {
    field: 'status',
    headerName: 'Status',
    valueFormatter: (value) => getStatusLabel(value as string),
  },
  {
    field: 'requestedAt',
    headerName: 'Requested',
    valueFormatter: (value) => new Date(value as string).toLocaleString(),
  },
];

export const StockTransferList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const quoteId = searchParams.get('quoteId');
  const { mode } = useContext(ColorModeContext);
  const animations = useAnimationStyles();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchValue, setSearchValue] = useState('');
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});

  const colors = mode === 'dark' ? {
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    border: palette.border,
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    surface: palette.light.surface,
    surfaceElevated: palette.light.surfaceElevated,
    border: palette.light.border,
    textPrimary: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  const { data: transfers = [], isLoading: transfersLoading } = useQuery({
    queryKey: ['stock-transfers'],
    queryFn: fetchStockTransfers,
  });

  const { data: locations = [] } = useQuery({
    queryKey: ['locations'],
    queryFn: fetchLocations,
  });

  // Fetch quote data when filtering by quoteId
  const { data: quoteForFilter } = useQuery({
    queryKey: ['quote', quoteId],
    queryFn: async () => {
      const response = await axiosInstance.get(`/quotes/${quoteId}`);
      return response.data;
    },
    enabled: !!quoteId,
  });

  const locationNameMap = useMemo(() => {
    const map = new Map<string, string>();
    locations.forEach((location) => map.set(location.id, location.name));
    return map;
  }, [locations]);

  const enrichedData = useMemo((): StockTransferWithLocation[] => {
    return transfers.map((transfer) => ({
      ...transfer,
      fromLocationName: locationNameMap.get(transfer.fromLocationId),
      toLocationName: locationNameMap.get(transfer.toLocationId),
    }));
  }, [transfers, locationNameMap]);

  const filterOptions: FilterOption[] = useMemo(() => [
    {
      field: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'pending', label: 'Pending' },
        { value: 'approved', label: 'Approved' },
        { value: 'in_transit', label: 'In Transit' },
        { value: 'partially_completed', label: 'Partially Completed' },
        { value: 'completed', label: 'Completed' },
        { value: 'cancelled', label: 'Cancelled' },
      ],
    },
    {
      field: 'isMultiProduct',
      label: 'Transfer Type',
      type: 'select',
      options: [
        { value: 'true', label: 'Multi-Product' },
        { value: 'false', label: 'Single Product' },
      ],
    },
  ], []);

  const filteredData = useMemo(() => {
    // Build set of transfer IDs from quote filter
    const quoteTransferIds = quoteId && quoteForFilter?.pendingTransferIds
      ? new Set(quoteForFilter.pendingTransferIds as string[])
      : null;

    return enrichedData.filter((item) => {
      // Filter by quote's pendingTransferIds if filtering by quote
      if (quoteTransferIds && !quoteTransferIds.has(item.id)) {
        return false;
      }

      if (searchValue) {
        const search = searchValue.toLowerCase();
        const productName = item.product?.name || '';
        const fromLocation = item.fromLocationName || '';
        const toLocation = item.toLocationName || '';
        const matchesSearch =
          productName.toLowerCase().includes(search) ||
          fromLocation.toLowerCase().includes(search) ||
          toLocation.toLowerCase().includes(search);
        if (!matchesSearch) return false;
      }

      if (activeFilters.status && item.status !== activeFilters.status) {
        return false;
      }

      if (activeFilters.isMultiProduct) {
        const isMulti = item.isMultiProduct === true;
        const filterValue = activeFilters.isMultiProduct === 'true';
        if (isMulti !== filterValue) {
          return false;
        }
      }

      return true;
    });
  }, [enrichedData, searchValue, activeFilters, quoteId, quoteForFilter]);

  const stats = useMemo(() => {
    const total = filteredData.length;
    const pending = filteredData.filter((item: any) => item.status === 'pending').length;
    const approved = filteredData.filter((item: any) => item.status === 'approved').length;
    const inTransit = filteredData.filter((item: any) => item.status === 'in_transit').length;
    const completed = filteredData.filter((item: any) => item.status === 'completed').length;
    const cancelled = filteredData.filter((item: any) => item.status === 'cancelled').length;
    const totalQuantity = filteredData.reduce((sum: number, item: any) => sum + (item.quantity || 0), 0);

    return { total, pending, approved, inTransit, completed, cancelled, totalQuantity };
  }, [filteredData]);

  const paginatedData = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredData.slice(start, start + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  const handleFilterChange = (field: string, value: string) => {
    setActiveFilters((prev) => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const handleClearFilters = () => {
    setActiveFilters({});
    setPage(0);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Column definitions for ResponsiveTable
  const tableColumns: Column<StockTransferWithLocation>[] = [
    {
      field: 'product',
      headerName: 'Product',
      render: (value, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {row.isMultiProduct ? (
            <>
              <Badge
                badgeContent={row.items?.length || 0}
                sx={{
                  '& .MuiBadge-badge': {
                    backgroundColor: palette.accent,
                    color: '#FFFFFF',
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.xs,
                    fontWeight: typography.fontWeight.bold,
                  },
                }}
              >
                <Inventory2Icon sx={{ color: palette.accent, fontSize: 20 }} />
              </Badge>
              <Box>
                <Typography
                  sx={{
                    color: colors.textPrimary,
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.sm,
                    fontWeight: typography.fontWeight.medium,
                  }}
                >
                  Multi-Product Transfer
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.textMuted,
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.xs,
                  }}
                >
                  {row.items?.length || 0} products
                </Typography>
              </Box>
            </>
          ) : (
            <Typography
              sx={{
                color: colors.textPrimary,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.sm,
                fontWeight: typography.fontWeight.medium,
              }}
            >
              {row.product?.name || 'Unknown'}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'quantity',
      headerName: 'Qty',
      render: (value, row) => (
        <Typography
          sx={{
            color: colors.textPrimary,
            fontFamily: typography.fontFamily.mono,
            fontSize: typography.fontSize.sm,
            fontWeight: typography.fontWeight.bold,
          }}
        >
          {row.isMultiProduct
            ? row.items?.reduce((sum: number, item: any) => sum + (item.quantity || 0), 0) || 0
            : row.quantity}
        </Typography>
      ),
    },
    {
      field: 'fromLocation',
      headerName: 'From',
      render: (value, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              backgroundColor: alpha(
                row.fromLocation?.type === 'warehouse' ? palette.primary : palette.accent,
                0.1
              ),
              border: `1px solid ${row.fromLocation?.type === 'warehouse' ? palette.primary : palette.accent}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {row.fromLocation?.type === 'warehouse' ? (
              <WarehouseIcon sx={{ fontSize: 16, color: palette.primary }} />
            ) : (
              <StoreIcon sx={{ fontSize: 16, color: palette.accent }} />
            )}
          </Box>
          <Box>
            <Typography
              variant="body2"
              sx={{
                color: colors.textPrimary,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.sm,
                fontWeight: typography.fontWeight.medium,
              }}
            >
              {row.fromLocationName || row.fromLocationId}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: colors.textMuted,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.xs,
                textTransform: 'uppercase',
              }}
            >
              {row.fromLocation?.type === 'warehouse' ? 'Warehouse' : 'Store'}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'toLocation',
      headerName: 'To',
      render: (value, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              backgroundColor: alpha(
                row.toLocation?.type === 'warehouse' ? palette.primary : palette.accent,
                0.1
              ),
              border: `1px solid ${row.toLocation?.type === 'warehouse' ? palette.primary : palette.accent}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {row.toLocation?.type === 'warehouse' ? (
              <WarehouseIcon sx={{ fontSize: 16, color: palette.primary }} />
            ) : (
              <StoreIcon sx={{ fontSize: 16, color: palette.accent }} />
            )}
          </Box>
          <Box>
            <Typography
              variant="body2"
              sx={{
                color: colors.textPrimary,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.sm,
                fontWeight: typography.fontWeight.medium,
              }}
            >
              {row.toLocationName || row.toLocationId}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: colors.textMuted,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.xs,
                textTransform: 'uppercase',
              }}
            >
              {row.toLocation?.type === 'warehouse' ? 'Warehouse' : 'Store'}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      render: (value, row) => {
        const statusColor = getStatusColor(row.status);
        const StatusIcon = getStatusIcon(row.status);

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                backgroundColor: alpha(statusColor, 0.1),
                border: `1px solid ${statusColor}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <StatusIcon sx={{ fontSize: 14, color: statusColor }} />
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: statusColor,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.xs,
                fontWeight: typography.fontWeight.bold,
                textTransform: 'uppercase',
              }}
            >
              {getStatusLabel(row.status)}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'requestedAt',
      headerName: 'Requested',
      render: (value) => (
        <Typography
          sx={{
            color: colors.textSecondary,
            fontFamily: typography.fontFamily.mono,
            fontSize: typography.fontSize.xs,
          }}
        >
          {new Date(value).toLocaleDateString()}
        </Typography>
      ),
    },
  ];

  return (
    <Box sx={{ ...animations.fadeIn }}>
      {/* Industrial Header */}
      <Box sx={{ ...animations.slideDown, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <LocalShippingIcon sx={{ color: palette.primary, fontSize: 24 }} />
            <Typography
              variant="overline"
              sx={{
                color: palette.primary,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.xs,
                letterSpacing: typography.letterSpacing.widest,
                fontWeight: typography.fontWeight.bold,
              }}
            >
              LOGISTICS OPERATIONS
            </Typography>
          </Box>
          <Typography
            variant="h3"
            sx={{
              fontFamily: typography.fontFamily.display,
              fontWeight: typography.fontWeight.bold,
              color: colors.textPrimary,
              textTransform: 'uppercase',
              letterSpacing: typography.letterSpacing.wide,
            }}
          >
            STOCK TRANSFERS
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/app/stock-transfers/create')}
          sx={{
            backgroundColor: palette.primary,
            color: '#FFFFFF',
            fontFamily: typography.fontFamily.mono,
            fontWeight: typography.fontWeight.bold,
            fontSize: typography.fontSize.sm,
            textTransform: 'uppercase',
            border: `2px solid ${palette.primary}`,
            borderRadius: theme.radius.xs,
            px: 3,
            py: 1.5,
            '&:hover': {
              backgroundColor: alpha(palette.primary, 0.9),
              borderColor: palette.primary,
            },
          }}
        >
          CREATE TRANSFER
        </Button>
      </Box>

      {/* Stats Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 3 }}>
        {/* Total Transfers */}
        <Card
          sx={{
            ...animations.staggeredCard(0, 0.2),
            position: 'relative',
            backgroundColor: colors.surfaceElevated,
            border: `2px solid ${colors.border}`,
            borderRadius: theme.radius.xs,
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              backgroundColor: palette.primary,
            },
            '&:hover': {
              borderColor: palette.primary,
              transform: 'translateY(-4px)',
              boxShadow: `0 8px 24px ${alpha(palette.primary, 0.2)}`,
            },
          }}
        >
          <Box sx={{ p: 2.5, pt: 3 }}>
            <Typography
              variant="overline"
              sx={{
                color: colors.textMuted,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.xs,
                fontWeight: typography.fontWeight.bold,
                letterSpacing: typography.letterSpacing.widest,
                textTransform: 'uppercase',
              }}
            >
              TOTAL TRANSFERS
            </Typography>
            <Typography
              variant="h3"
              sx={{
                color: palette.primary,
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.extrabold,
                fontSize: typography.fontSize['3xl'],
                mt: 1,
              }}
            >
              {stats.total}
            </Typography>
          </Box>
        </Card>

        {/* Pending */}
        <Card
          sx={{
            ...animations.staggeredCard(1, 0.2),
            position: 'relative',
            backgroundColor: colors.surfaceElevated,
            border: `2px solid ${colors.border}`,
            borderRadius: theme.radius.xs,
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              backgroundColor: palette.warning,
            },
            '&:hover': {
              borderColor: palette.warning,
              transform: 'translateY(-4px)',
              boxShadow: `0 8px 24px ${alpha(palette.warning, 0.2)}`,
            },
          }}
        >
          <Box sx={{ p: 2.5, pt: 3 }}>
            <Typography
              variant="overline"
              sx={{
                color: colors.textMuted,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.xs,
                fontWeight: typography.fontWeight.bold,
                letterSpacing: typography.letterSpacing.widest,
                textTransform: 'uppercase',
              }}
            >
              PENDING
            </Typography>
            <Typography
              variant="h3"
              sx={{
                color: palette.warning,
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.extrabold,
                fontSize: typography.fontSize['3xl'],
                mt: 1,
              }}
            >
              {stats.pending}
            </Typography>
          </Box>
        </Card>

        {/* In Transit */}
        <Card
          sx={{
            ...animations.staggeredCard(2, 0.2),
            position: 'relative',
            backgroundColor: colors.surfaceElevated,
            border: `2px solid ${colors.border}`,
            borderRadius: theme.radius.xs,
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              backgroundColor: palette.info,
            },
            '&:hover': {
              borderColor: palette.info,
              transform: 'translateY(-4px)',
              boxShadow: `0 8px 24px ${alpha(palette.info, 0.2)}`,
            },
          }}
        >
          <Box sx={{ p: 2.5, pt: 3 }}>
            <Typography
              variant="overline"
              sx={{
                color: colors.textMuted,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.xs,
                fontWeight: typography.fontWeight.bold,
                letterSpacing: typography.letterSpacing.widest,
                textTransform: 'uppercase',
              }}
            >
              IN TRANSIT
            </Typography>
            <Typography
              variant="h3"
              sx={{
                color: palette.info,
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.extrabold,
                fontSize: typography.fontSize['3xl'],
                mt: 1,
              }}
            >
              {stats.inTransit}
            </Typography>
          </Box>
        </Card>

        {/* Completed */}
        <Card
          sx={{
            ...animations.staggeredCard(3, 0.2),
            position: 'relative',
            backgroundColor: colors.surfaceElevated,
            border: `2px solid ${colors.border}`,
            borderRadius: theme.radius.xs,
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              backgroundColor: palette.success,
            },
            '&:hover': {
              borderColor: palette.success,
              transform: 'translateY(-4px)',
              boxShadow: `0 8px 24px ${alpha(palette.success, 0.2)}`,
            },
          }}
        >
          <Box sx={{ p: 2.5, pt: 3 }}>
            <Typography
              variant="overline"
              sx={{
                color: colors.textMuted,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.xs,
                fontWeight: typography.fontWeight.bold,
                letterSpacing: typography.letterSpacing.widest,
                textTransform: 'uppercase',
              }}
            >
              COMPLETED
            </Typography>
            <Typography
              variant="h3"
              sx={{
                color: palette.success,
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.extrabold,
                fontSize: typography.fontSize['3xl'],
                mt: 1,
              }}
            >
              {stats.completed}
            </Typography>
          </Box>
        </Card>

        {/* Cancelled */}
        <Card
          sx={{
            ...animations.staggeredCard(4, 0.2),
            position: 'relative',
            backgroundColor: colors.surfaceElevated,
            border: `2px solid ${colors.border}`,
            borderRadius: theme.radius.xs,
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              backgroundColor: palette.error,
            },
            '&:hover': {
              borderColor: palette.error,
              transform: 'translateY(-4px)',
              boxShadow: `0 8px 24px ${alpha(palette.error, 0.2)}`,
            },
          }}
        >
          <Box sx={{ p: 2.5, pt: 3 }}>
            <Typography
              variant="overline"
              sx={{
                color: colors.textMuted,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.xs,
                fontWeight: typography.fontWeight.bold,
                letterSpacing: typography.letterSpacing.widest,
                textTransform: 'uppercase',
              }}
            >
              CANCELLED
            </Typography>
            <Typography
              variant="h3"
              sx={{
                color: palette.error,
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.extrabold,
                fontSize: typography.fontSize['3xl'],
                mt: 1,
              }}
            >
              {stats.cancelled}
            </Typography>
          </Box>
        </Card>

        {/* Total Units */}
        <Card
          sx={{
            ...animations.staggeredCard(5, 0.2),
            position: 'relative',
            backgroundColor: colors.surfaceElevated,
            border: `2px solid ${colors.border}`,
            borderRadius: theme.radius.xs,
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              backgroundColor: palette.accent,
            },
            '&:hover': {
              borderColor: palette.accent,
              transform: 'translateY(-4px)',
              boxShadow: `0 8px 24px ${alpha(palette.accent, 0.2)}`,
            },
          }}
        >
          <Box sx={{ p: 2.5, pt: 3 }}>
            <Typography
              variant="overline"
              sx={{
                color: colors.textMuted,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.xs,
                fontWeight: typography.fontWeight.bold,
                letterSpacing: typography.letterSpacing.widest,
                textTransform: 'uppercase',
              }}
            >
              TOTAL UNITS
            </Typography>
            <Typography
              variant="h3"
              sx={{
                color: palette.accent,
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.extrabold,
                fontSize: typography.fontSize['3xl'],
                mt: 1,
              }}
            >
              {stats.totalQuantity.toLocaleString()}
            </Typography>
          </Box>
        </Card>
      </Box>

      {/* Quote Filter Banner */}
      {quoteId && quoteForFilter && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            backgroundColor: alpha(palette.info, 0.1),
            border: `1px solid ${palette.info}`,
            borderRadius: theme.radius.xs,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterListIcon sx={{ color: palette.info }} />
            <Typography
              sx={{
                color: colors.textPrimary,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.sm,
              }}
            >
              Showing transfers for Quote <strong>{quoteForFilter.quoteNumber}</strong>
            </Typography>
          </Box>
          <Button
            size="small"
            onClick={() => setSearchParams({})}
            startIcon={<ClearIcon />}
            sx={{
              color: palette.info,
              borderColor: palette.info,
              '&:hover': {
                backgroundColor: alpha(palette.info, 0.1),
              },
            }}
          >
            Show All Transfers
          </Button>
        </Box>
      )}

      {/* Industrial Table Card */}
      <Card
        sx={{
          ...animations.slideUp(0.68),
          backgroundColor: colors.surface,
          border: `2px solid ${colors.border}`,
          borderRadius: theme.radius.sm,
        }}
      >
        <Box sx={{ p: 2, borderBottom: `2px solid ${colors.border}` }}>
          <TableToolbar
            title="Stock Transfers"
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            searchPlaceholder="Search by product, location..."
            filters={filterOptions}
            activeFilters={activeFilters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            data={filteredData}
            columns={exportColumns}
            exportFileName="stock-transfers"
          />
        </Box>

        <ResponsiveTable
          columns={tableColumns}
          rows={paginatedData}
          loading={transfersLoading}
          emptyMessage="NO TRANSFERS FOUND"
          primaryField="product"
          getPrimaryText={(row) => {
            if (row.isMultiProduct) {
              return `${row.items?.length || 0} Products`;
            }
            return row.items?.[0]?.product?.name || row.product?.name || 'Unknown Product';
          }}
          onRowClick={(transfer) => navigate(`/app/stock-transfers/${transfer.id}`)}
        />

        <TablePagination
          component="div"
          count={filteredData.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Card>
    </Box>
  );
};
